import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  selectContact,
  selectContactChange,
  selectIsLoadingContactChange,
  selectSegmentLoadingStates,
  selectSelectedKeys,
  setSelectedKeys,
} from '../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  DEFAULT_SEARCH_KEY,
  filterIgnoreFieldsFromKeys,
  gatherAllChildSuccessfulFields,
  getAllKeysInData,
  getPreviousValue,
  SEARCH_KEY_MAP,
  SEPARATOR,
} from './utils'
import { useGetContactSchemaQuery } from '../../store/integrations/integrations'
import { selectAccountId } from '../../store/auth/authSlice'
import { ClickNavigation, ClickNavigationSection } from '../../components/NavigationWithSideBar/ClickNavigation'
import { UpdateExternalContactForm } from './UpdateExternalContactForm'
import { IntegrationServiceId } from '../../common/integrations'
import { UpToDate } from './ExternalContactUpdateIsUpToDate'
import { ContactChange } from '../../models/MeetingSupport'
import { DataProcessingView } from './InitCreateOrUpdateExternalContact/DataProcessing/DataProcessingView'
import { getPanelInfos } from './hooks'
import { NavigationSidebarSection } from '../../components/NavigationWithSideBar/NavigationSidebar'
import { ServiceSpecificContactUpdateSections } from './ServiceSpecificCreateOrUpdateExternalContact/ServiceSpecificContactUpdateSections'

import './SegmentedExternalContactUpdateBody.scss'
import { DisabledContactUpdateSection } from './DisabledContactUpdateSection'
import { useContactUpdateHistory } from './ContactUpdateHistoryTypes'

const countLeafFields = (obj: unknown, depth = 0): number => {
  if (depth > 10 || !obj || typeof obj !== 'object') {
    return obj !== null && obj !== undefined && obj !== '' ? 1 : 0
  }

  if (Array.isArray(obj)) {
    return obj.reduce((sum: number, item) => sum + countLeafFields(item, depth + 1), 0)
  }

  return Object.values(obj).reduce((sum: number, value) => sum + countLeafFields(value, depth + 1), 0)
}

export interface SegmentedExternalContactUpdateBodyProps {
  serviceId: IntegrationServiceId
  onKeySelected?: (selectedKey: string, isUnselect: boolean) => void
  activeSectionId?: string
  onActiveSectionIdChange?: (id: string) => void
  onSectionsLoaded?: (sections: NavigationSidebarSection[]) => void
  readonly?: boolean
  enableExplainer?: boolean
}

interface SegmentedExternalContactUpdateContentProps extends SegmentedExternalContactUpdateBodyProps {
  extraSections?: ClickNavigationSection[]
}

const SegmentedExternalContactUpdateContent: React.FC<SegmentedExternalContactUpdateContentProps> = ({
  serviceId,
  onKeySelected,
  activeSectionId: controlledActiveId,
  onActiveSectionIdChange,
  onSectionsLoaded,
  readonly = false,
  enableExplainer = false,
  extraSections,
}) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const contact = useAppSelector(selectContact)
  const isLoadingContactChange = useAppSelector(selectIsLoadingContactChange)
  const contactChange = useAppSelector(selectContactChange)
  const selectedKeys = useAppSelector(selectSelectedKeys)
  const segmentLoadingStates = useAppSelector(selectSegmentLoadingStates)
  const historyContext = useContactUpdateHistory()

  const [sectionErrors, setSectionErrors] = useState<Record<string, { hasErrors: boolean; errorCount: number }>>({})

  const effectiveContactChange = useMemo(() => {
    if (historyContext?.isHistoryMode && historyContext.historyData && historyContext.historyData.serviceId === serviceId) {
      return {
        contactValues: historyContext.historyData.contactValues,
        previousContactValues: historyContext.historyData.previousContactValues ?? {},
        contactSchema: historyContext.historyData.contactSchema,
        metadata: {},
      } as ContactChange
    }
    return contactChange
  }, [historyContext, contactChange, serviceId])

  const { data: contactSchemaResponse } = useGetContactSchemaQuery({ accountId, connector: serviceId, context: { objectName: contact?.type ?? 'contact' } })

  const externalInfo = contact?.externalInfos.find((e) => e.serviceId === serviceId)

  const contactSchemaToShow = useMemo(() => {
    if (historyContext?.isHistoryMode && historyContext.historyData && historyContext.historyData.serviceId === serviceId) {
      if (Object.keys(historyContext.historyData.contactSchema).length === 0 && effectiveContactChange?.contactValues) {
        const generatedSchema: Record<string, unknown> = {}

        Object.keys(effectiveContactChange.contactValues).forEach((key) => {
          const capitalizedName = key.charAt(0).toUpperCase() + key.slice(1)
          generatedSchema[key] = {
            name: capitalizedName,
            type: 'object',
          }
        })

        return generatedSchema
      }

      return historyContext.historyData.contactSchema
    }

    if (!contactSchemaResponse) {
      return null
    }

    if (externalInfo) {
      return externalInfo.contactSchema
    }

    return contactSchemaResponse.data
  }, [historyContext, externalInfo, contactSchemaResponse, serviceId, effectiveContactChange])

  const hasUncheckedSuccessfulFields = useRef(false)

  useEffect(() => {
    if (!effectiveContactChange || !contactSchemaToShow || !selectedKeys || !selectedKeys.length || hasUncheckedSuccessfulFields.current) {
      return
    }
    const { contactValues, metadata } = effectiveContactChange

    if (externalInfo) {
      const fieldsToDeselect = Object.entries(externalInfo.contactSchema).reduce((acc: string[], [key, panel]): string[] => {
        return [...acc, ...gatherAllChildSuccessfulFields(contactValues[key], key, panel, metadata, Object.keys(contactValues[key] ?? {}), 'contactValues')]
      }, [])
      dispatch(setSelectedKeys(selectedKeys.filter((key) => !fieldsToDeselect.includes(key))))
    }

    hasUncheckedSuccessfulFields.current = true
  }, [effectiveContactChange, contactSchemaToShow, externalInfo, selectedKeys, dispatch])

  const handleValidationChange = useCallback((key: string, hasErrors: boolean, errorCount: number) => {
    setSectionErrors((prev) => {
      const current = prev[key]
      if (!current || current.hasErrors !== hasErrors) {
        return {
          ...prev,
          [key]: { hasErrors, errorCount },
        }
      }
      return prev
    })
  }, [])

  const navigationSections: ClickNavigationSection[] = useMemo(() => {
    if (!contactSchemaToShow) {
      return []
    }

    const {
      contactSchema,
      contactValues,
      previousContactValues,
      metadata = {},
    } = effectiveContactChange || {
      contactSchema: {},
      contactValues: {},
      previousContactValues: {},
      metadata: {},
    }

    const initialValues = effectiveContactChange?.contactValues || {}

    const schemaSections = Object.entries(contactSchemaToShow)
      .map(([key, panel]) => {
        const panelValues = key in contactValues ? (contactValues as Record<string, unknown>)[key] : undefined
        const isSegmentLoading = segmentLoadingStates[key] ?? false

        const { errorMessages } = getPanelInfos({
          panel,
          metadata,
          contactSchema,
          contactValues: contactValues || {},
          key,
          previousContactValues,
        })

        const isMatchingHistoryModeForSection = historyContext?.isHistoryMode && historyContext.historyData?.serviceId === serviceId

        if (!isMatchingHistoryModeForSection && metadata && !errorMessages.length && !isSegmentLoading) {
          return null
        }

        if (isMatchingHistoryModeForSection && panelValues) {
          const hasDataCheck = Object.keys(panelValues as Record<string, unknown>).length > 0
          if (!hasDataCheck) {
            return null
          }
        }

        const panelRootPath = `contactValues.${key}`
        const searchKey = SEARCH_KEY_MAP[serviceId] ?? DEFAULT_SEARCH_KEY

        const allKeysInPanel = getAllKeysInData(panelValues, panelRootPath)
        const selectedKeysInPanel = allKeysInPanel.filter((k) => selectedKeys.includes(k))
        const validSelectedKeysCount = filterIgnoreFieldsFromKeys(serviceId, selectedKeysInPanel).filter((key) => {
          const parts = key.split(SEPARATOR)
          const valueParts = parts.slice(1)

          const previousValue = getPreviousValue(contactValues, previousContactValues, valueParts, searchKey)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const currentValue = valueParts.reduce((acc: any, part) => (acc ? acc[part] : undefined), contactValues)

          return JSON.stringify(previousValue) !== JSON.stringify(currentValue)
        }).length

        const initialPanelData = initialValues[key]
        const allInitialKeys = getAllKeysInData(initialPanelData, panelRootPath)
        const validInitialKeysCount = filterIgnoreFieldsFromKeys(serviceId, allInitialKeys).length

        const baseLabel = (panel as Record<string, unknown>)?.title || panel.name || key.charAt(0).toUpperCase() + key.slice(1)

        const displayCount = isMatchingHistoryModeForSection && panelValues ? countLeafFields(panelValues) : validSelectedKeysCount

        const label = `${baseLabel} (${displayCount})`
        const sectionError = sectionErrors[key] || { hasErrors: false, errorCount: 0 }

        const isGloballyLoading = isLoadingContactChange

        const hasData =
          isMatchingHistoryModeForSection && panelValues
            ? displayCount > 0
            : validInitialKeysCount > 0 || validSelectedKeysCount > 0 || allKeysInPanel.length > 0

        const isDisabled = isMatchingHistoryModeForSection ? displayCount === 0 : !isGloballyLoading && !isSegmentLoading && !hasData

        return {
          id: key,
          label: label,
          disabled: isDisabled,
          hasError: !readonly && (errorMessages.length > 0 || sectionError.hasErrors),
          isLoading: isSegmentLoading,
          content: () => {
            if (isSegmentLoading) {
              return (
                <div className='h-100'>
                  <DataProcessingView
                    serviceId={serviceId}
                    title={`${baseLabel} details are still loading.`}
                    subtitle='You can already review completed items.'
                  />
                </div>
              )
            }

            if (isDisabled) {
              return <DisabledContactUpdateSection title={`${baseLabel} details are up to date`} />
            }

            const segmentSchema =
              (contactSchema && typeof contactSchema === 'object' && key in contactSchema ? (contactSchema as Record<string, unknown>)[key] : null) ||
              contactSchemaToShow[key]

            return (
              <UpdateExternalContactForm
                serviceId={serviceId}
                rootFieldId={panelRootPath}
                contactSchema={segmentSchema}
                contactValues={(panelValues as Record<string, unknown>) || ({} as { [key: string]: unknown })}
                previousContactValues={
                  ((previousContactValues as Record<string, unknown>)?.[key] as Record<string, unknown>) ||
                  ({} as {
                    [key: string]: unknown
                  })
                }
                requiredSteps={panel?.['required_operation_steps'] ?? {}}
                metadata={metadata}
                onKeySelected={onKeySelected}
                hideRootFieldHeader={false}
                title={panel.name}
                onValidationChange={(hasErrors, errorCount) => handleValidationChange(key, hasErrors, errorCount)}
                readonly={readonly}
                isContactCreate={isMatchingHistoryModeForSection ? !historyContext?.historyData?.previousContactValues : undefined}
                enableExplainer={enableExplainer}
              />
            )
          },
        }
      })
      .filter((section) => section !== null) as ClickNavigationSection[]

    const hasVisibleSchemaSection = schemaSections.some((section) => !section.disabled)
    const isSavedChange = !!metadata
    return hasVisibleSchemaSection || !isSavedChange ? [...schemaSections, ...(extraSections ?? [])] : schemaSections
  }, [
    historyContext,
    effectiveContactChange,
    contactSchemaToShow,
    serviceId,
    onKeySelected,
    selectedKeys,
    handleValidationChange,
    sectionErrors,
    segmentLoadingStates,
    isLoadingContactChange,
    readonly,
    enableExplainer,
    extraSections,
  ])

  useEffect(() => {
    if (onSectionsLoaded && navigationSections.length > 0) {
      onSectionsLoaded(navigationSections)
    }
  }, [navigationSections, onSectionsLoaded])

  const isMatchingHistoryMode = historyContext?.isHistoryMode && historyContext.historyData?.serviceId === serviceId
  const shouldShowFullLoader = isMatchingHistoryMode ? false : !contactSchemaToShow || isLoadingContactChange

  if (shouldShowFullLoader) {
    return <DataProcessingView serviceId={serviceId} />
  }

  const isAnySegmentLoading = Object.values(segmentLoadingStates).some(Boolean)
  const hasVisibleSections = navigationSections.find((section) => !section.disabled)

  const shouldShowUpToDate = isMatchingHistoryMode ? false : !isLoadingContactChange && !isAnySegmentLoading && (!effectiveContactChange || !hasVisibleSections)

  if (shouldShowUpToDate) {
    return <UpToDate serviceId={serviceId} />
  }

  const existingControlledActiveId = navigationSections.find((s) => s.id === controlledActiveId)?.id

  const activeIdToUse = isMatchingHistoryMode
    ? existingControlledActiveId || navigationSections.find((s) => !s.disabled)?.id || navigationSections[0]?.id
    : existingControlledActiveId || navigationSections[0]?.id

  const handleSectionClick = (id: string) => {
    if (onActiveSectionIdChange) {
      onActiveSectionIdChange(id)
    }
  }

  return (
    <div className='flex-grow-1 overflow-auto d-flex flex-column segmented-external-contact-update-body'>
      <ClickNavigation
        sections={navigationSections}
        onSectionClick={handleSectionClick}
        defaultActiveSectionId={activeIdToUse}
        activeSectionId={activeIdToUse}
        className='h-100'
      />
    </div>
  )
}

const MemoizedSegmentedExternalContactUpdateContent = React.memo(SegmentedExternalContactUpdateContent)

export const SegmentedExternalContactUpdateBody: React.FC<SegmentedExternalContactUpdateBodyProps> = (props) => {
  if (props.readonly) {
    return <MemoizedSegmentedExternalContactUpdateContent {...props} />
  }

  return (
    <ServiceSpecificContactUpdateSections serviceId={props.serviceId}>
      {(extraSections) => <MemoizedSegmentedExternalContactUpdateContent {...props} extraSections={extraSections} />}
    </ServiceSpecificContactUpdateSections>
  )
}
